import {
  ArrowDownUp,
  ArrowLeftRight,
  Maximize,
  SquareDashedBottom,
} from "lucide-react";

import { IgDivider } from "@intelligems/ig-theme";
import type { TextComponent } from "@src/store/slices/experienceBuilder/types";
import { InputAdornment } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { RichTextEditor } from "@src/components/RichTextEditor";
import { Panel } from "@src/features/experienceBuilder/modifications/common/panel/Panel";
import { useDispatch, useSelector } from "@src/store";
import {
  ExperienceBuilderAction as Action,
  ExperienceBuilderSelector as Selector,
} from "@src/store/slices/experienceBuilder";
import { assert } from "@src/utils/assert";
import { ModificationHeader } from "@src/features/experienceBuilder/modifications/common/ModificationHeader";
import React from "react";

export const TextComponentPanel = () => {
  const dispatch = useDispatch();

  const mod = useSelector(Selector.component.getVariationDraft) as
    | TextComponent
    | undefined;
  assert(mod !== undefined, "mod is undefined");
  const isApplicable = useSelector(Selector.isVariationDraftApplicable);

  const update = (changes: Partial<TextComponent>) =>
    dispatch(Action.component.setDraft({ ...mod, ...changes }));
  const cancel = () => void dispatch(Action.cancelDraft({ trackCancel: true }));
  const apply = () => void dispatch(Action.applyDraft());

  return (
    <>
      <ModificationHeader />
      <Panel>
        <Panel.Body>
          {/* Text content */}
          <Stack spacing={1}>
            <Panel.Header>Text content</Panel.Header>
            <RichTextEditor
              value={mod.content}
              onChange={(content) => update({ content })}
              disabled={false}
              placeholder="Add your text here"
              minHeight="4em"
            />
          </Stack>

          <IgDivider />

          {/* Spacing */}
          <Stack spacing={2}>
            <Panel.Label>Spacing</Panel.Label>
            <Grid container spacing={2}>
              <Grid size={6}>
                <Panel.TextField
                  label="Line height"
                  value={mod.lineHeight}
                  onChange={(e) => update({ lineHeight: e.target.value })}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <ArrowDownUp size={16} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Grid size={6}>
                <Panel.TextField
                  label="Letter spacing"
                  value={mod.letterSpacing}
                  onChange={(e) => update({ letterSpacing: e.target.value })}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <ArrowLeftRight size={16} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Grid size={6}>
                <Panel.TextField
                  label="Padding"
                  value={mod.padding}
                  onChange={(e) => update({ padding: e.target.value })}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SquareDashedBottom size={16} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Grid size={6}>
                <Panel.TextField
                  label="Margin"
                  value={mod.margin}
                  onChange={(e) => update({ margin: e.target.value })}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Maximize size={16} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Stack>
        </Panel.Body>

        <Panel.Buttons>
          <Panel.CancelButton onClick={() => void cancel()} />
          <Panel.ApplyButton onClick={apply} disabled={!isApplicable} />
        </Panel.Buttons>
      </Panel>
    </>
  );
};
